import { create } from 'zustand';
import { ColumnState } from 'ag-grid-community';
import { getCurrentMonthDateRange } from 'src/utils/date/dateUtils';
import { DateRange } from 'react-day-picker';

// 탭 타입 정의 및 UI 상태 타입 (생산실적)
export type TabType = 'date' | 'item' | 'workplace';

export interface TabConfig {
    id: TabType;
    label: string;
    description: string;
}

export type DateStockData = any;
export type ItemStockData = any;
export type WorkplaceStockData = any;   

export interface TabData {
    date: DateStockData[];
    item: ItemStockData[];
    workplace: WorkplaceStockData[];
}

export interface TabColumnStates {
    date: ColumnState[];
    item: ColumnState[];
    workplace: ColumnState[];
}

export interface TabLoadingStates {
    date: boolean;
    item: boolean;
    workplace: boolean;
}

// 탭별 선택된 행 ID 목록
export interface TabSelectedIds {
    date: string[];
    item: string[];
    workplace: string[];
}

export interface TabScrollTop {
    date: number | null;
    item: number | null;
    workplace: number | null;
}

const defaultTabScrollTop = (): TabScrollTop => ({
    date: null,
    item: null,
    workplace: null,
});

// 필터 인터페이스 (API에서 사용)
export interface TQC2020_R01Filter {
    compCd: string;
    workplaceCd: string;
    itemCd: string;
    itemNm: string;
    inspectEmp: string;
    workerEmp: string;
    workerEmpNm: string;
    badCase: string;
    productDt: DateRange | undefined;
    startDate: string;
    endDate: string;
    itemGroup: string;
    brandCd: string;
    searchText: string;
}

interface TQC2020_R01Store {
    // 기본 필터
    TQC2020_R01Filter: TQC2020_R01Filter;
    setTQC2020_R01Filter: (filter: Partial<TQC2020_R01Filter>) => void;
    
    
    // 활성 탭
    activeTab: TabType;
    setActiveTab: (tab: TabType) => void;
    
    // 탭별 데이터
    tabData: TabData;
    setTabData: (tabType: TabType, data: any[]) => void;
    
    // 탭별 컬럼 상태
    tabColumnStates: TabColumnStates;
    setTabColumnState: (tabType: TabType, columnState: any[]) => void;
    
    // 탭별 로딩 상태
    tabLoadingStates: TabLoadingStates;
    setTabLoadingState: (tabType: TabType, loading: boolean) => void;
    
    // 탭별 선택 상태
    tabSelectedIds: TabSelectedIds;
    setTabSelectedIds: (tabType: TabType, ids: string[]) => void;
    toggleSelectedId: (tabType: TabType, id: string) => void;
    clearTabSelectedIds: (tabType: TabType) => void;

    tabScrollTop: TabScrollTop;
    setTabScrollTop: (tabType: TabType, top: number | null) => void;
    resetAllTabScrollTop: () => void;
    
    // 초기화
    initData: () => void;
}

export const defaultTQC2020_R01Filter: TQC2020_R01Filter =  {
	compCd: '',
	workplaceCd: '',
	productDt: getCurrentMonthDateRange(),
	itemCd: '',
	itemNm: '',
	inspectEmp: '',
    workerEmp: '',
	workerEmpNm: '',
	badCase: '',
	startDate: '',
	endDate: '',
	itemGroup: '',
	brandCd: '',
	searchText: '',
}
export const useTQC2020_R01Store = create<TQC2020_R01Store>((set, get) => ({
    // 기본 필터
    TQC2020_R01Filter: defaultTQC2020_R01Filter,
    setTQC2020_R01Filter: (filter) => set((state) => ({
        TQC2020_R01Filter: { ...state.TQC2020_R01Filter, ...filter }
    })),
    
    // 활성 탭
    activeTab: 'date',
    setActiveTab: (tab) => {
        set({ activeTab: tab });
    },
    
    // 탭별 데이터
    tabData: {
        date: [],
        item: [],
        workplace: [],
    },
    setTabData: (tabType, data) => set((state) => ({
        tabData: {
            ...state.tabData,
            [tabType]: data
        }
    })),
    
    // 탭별 컬럼 상태
    tabColumnStates: {
        date: [],
        item: [],
        workplace: [],
    },
    setTabColumnState: (tabType, columnState) => set((state) => ({
        tabColumnStates: {
            ...state.tabColumnStates,
            [tabType]: columnState
        }
    })),
    
    // 탭별 로딩 상태
    tabLoadingStates: {
        date: false,
        item: false,
        workplace: false,
    },
    setTabLoadingState: (tabType, loading) => set((state) => ({
        tabLoadingStates: {
            ...state.tabLoadingStates,
            [tabType]: loading
        }
    })),
    
    // 탭별 선택 상태
    tabSelectedIds: {
        date: [],
        item: [],
        workplace: [],
    },
    setTabSelectedIds: (tabType, ids) => set((state) => ({
        tabSelectedIds: {
            ...state.tabSelectedIds,
            [tabType]: ids
        }
    })),
    toggleSelectedId: (tabType, id) => set((state) => {
        const current = state.tabSelectedIds[tabType] || [];
        const exists = current.includes(id);
        const next = exists ? current.filter(v => v !== id) : [...current, id];
        return {
            tabSelectedIds: {
                ...state.tabSelectedIds,
                [tabType]: next
            }
        } as any;
    }),
    clearTabSelectedIds: (tabType) => set((state) => ({
        tabSelectedIds: {
            ...state.tabSelectedIds,
            [tabType]: []
        }
    })),

    tabScrollTop: defaultTabScrollTop(),
    setTabScrollTop: (tabType, top) => set((state) => ({
        tabScrollTop: {
            ...state.tabScrollTop,
            [tabType]: top,
        },
    })),
    resetAllTabScrollTop: () => set({ tabScrollTop: defaultTabScrollTop() }),
    
    // 초기화
    initData: () => set({
        TQC2020_R01Filter: defaultTQC2020_R01Filter,
        activeTab: 'date',
        tabData: {
            date: [],
            item: [],
            workplace: [],
        },
        tabColumnStates: {
            date: [],
            item: [],
            workplace: [],
        },
        tabLoadingStates: {
            date: false,
            item: false,
            workplace: false,
        },
        tabSelectedIds: {
            date: [],
            item: [],
            workplace: [],
        },
        tabScrollTop: defaultTabScrollTop(),
    }),
}));

export default useTQC2020_R01Store;
